/* Aerfa Studio: progressive motion and native shopping interactions. */
(() => {
  'use strict';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const language = document.documentElement.lang;
  const copy = language.startsWith('ja')
    ? { previous: '前の商品', next: '次の商品', rail: '商品一覧。左右の矢印キーでも移動できます', top: 'ページの先頭へ' }
    : language.startsWith('zh')
      ? { previous: '上一组商品', next: '下一组商品', rail: '商品列表，可用左右方向键浏览', top: '返回顶部' }
      : { previous: 'Previous products', next: 'Next products', rail: 'Products. Use left and right arrow keys to browse', top: 'Back to top' };
  const seen = new WeakSet();
  const animations = new Set();
  const rails = new Map();
  const ease = 'cubic-bezier(.22,1,.36,1)';
  const all = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const animate = (element, delay = 0) => {
    if (reduced.matches || !element.animate) return;
    const animation = element.animate([
      { opacity: 0, transform: 'translateY(18px)' },
      { opacity: 1, transform: 'translateY(0)' }
    ], { duration: 650, delay, easing: ease, fill: 'backwards' });
    animations.add(animation);
    animation.finished.catch(() => {}).finally(() => animations.delete(animation));
  };
  const reveal = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      reveal.unobserve(entry.target);
      const siblings = Array.from(entry.target.parentElement.children);
      animate(entry.target, Math.min(siblings.indexOf(entry.target) % 4, 3) * 65);
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -24px 0px' }) : null;

  function prepareReveals(root) {
    all('[data-aerfa-reveal], .aerfa-product-card, .aerfa-product-story__copy, .aerfa-product-detail, .newsletter-section .newsletter-form-info', root).forEach(element => {
      element.classList.add('is-visible');
      if (seen.has(element)) return;
      seen.add(element);
      if (element.matches('.aerfa-hero__content')) {
        Array.from(element.children).forEach((child, index) => animate(child, 70 + index * 85));
      } else if (!element.parentElement.closest('[data-aerfa-reveal]')) {
        reveal?.observe(element);
      }
    });
  }

  const arrow = direction => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="${direction < 0 ? 'M19 12H5m6-6-6 6 6 6' : 'M5 12h14m-6-6 6 6-6 6'}"/></svg>`;
  function prepareRail(rail, controls) {
    if (rails.has(rail)) return;
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const previous = document.createElement('button');
    const next = document.createElement('button');
    const meter = document.createElement('span');
    meter.className = 'aerfa-rail-meter';
    meter.setAttribute('aria-hidden', 'true');
    meter.innerHTML = '<span></span>';
    [previous, next].forEach((button, index) => {
      button.type = 'button';
      button.className = 'aerfa-rail-button';
      button.setAttribute('aria-label', index ? copy.next : copy.previous);
      button.setAttribute('aria-controls', rail.id);
      button.innerHTML = arrow(index ? 1 : -1);
    });
    controls.replaceChildren(previous, meter, next);
    rail.setAttribute('role', 'region');
    rail.setAttribute('aria-label', copy.rail);
    let frame = 0;
    function update() {
      frame = 0;
      const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const overflow = maximum > 2;
      controls.hidden = !overflow;
      rail.classList.toggle('aerfa-can-drag', overflow);
      if (overflow) rail.tabIndex = 0;
      else rail.removeAttribute('tabindex');
      previous.disabled = rail.scrollLeft <= 3;
      next.disabled = rail.scrollLeft >= maximum - 3;
      meter.firstElementChild.style.transform = `scaleX(${maximum ? Math.min(1, .15 + .85 * rail.scrollLeft / maximum) : 1})`;
    }
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    function move(direction) {
      const cards = all('.aerfa-product-card', rail);
      const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : rail.clientWidth * .85;
      rail.scrollBy({ left: direction * step, behavior: reduced.matches ? 'instant' : 'smooth' });
    }
    previous.addEventListener('click', () => move(-1), options);
    next.addEventListener('click', () => move(1), options);
    rail.addEventListener('scroll', schedule, { ...options, passive: true });
    rail.addEventListener('keydown', event => {
      if (event.target !== rail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home' || event.key === 'End') rail.scrollTo({ left: event.key === 'Home' ? 0 : rail.scrollWidth, behavior: reduced.matches ? 'instant' : 'smooth' });
      else move(event.key === 'ArrowLeft' ? -1 : 1);
    }, options);
    let drag = null;
    let suppressClick = false;
    rail.addEventListener('pointerdown', event => {
      suppressClick = false;
      if (event.pointerType !== 'mouse' || event.button !== 0 || !fine.matches || event.target.closest('button, input, select') || rail.scrollWidth <= rail.clientWidth + 2) return;
      drag = { x: event.clientX, y: event.clientY, left: rail.scrollLeft, id: event.pointerId, moved: false };
    }, options);
    rail.addEventListener('dragstart', event => { if (drag) event.preventDefault(); }, options);
    rail.addEventListener('pointermove', event => {
      if (!drag) return;
      const dx = event.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(event.clientY - drag.y)) {
        drag.moved = true;
        rail.setPointerCapture(drag.id);
        rail.classList.add('is-dragging');
      }
      if (drag.moved) { event.preventDefault(); rail.scrollLeft = drag.left - dx; }
    }, options);
    const finish = () => {
      if (!drag) return;
      suppressClick = drag.moved;
      if (rail.hasPointerCapture(drag.id)) rail.releasePointerCapture(drag.id);
      drag = null;
      rail.classList.remove('is-dragging');
      schedule();
    };
    rail.addEventListener('pointerup', finish, options);
    rail.addEventListener('pointercancel', finish, options);
    rail.addEventListener('lostpointercapture', finish, options);
    rail.addEventListener('pointerleave', () => { if (drag && !drag.moved) drag = null; }, options);
    rail.addEventListener('click', event => {
      if (!suppressClick) return;
      suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, { ...options, capture: true });
    const resize = 'ResizeObserver' in window ? new ResizeObserver(schedule) : null;
    resize?.observe(rail);
    window.addEventListener('resize', schedule, options);
    update();
    rails.set(rail, () => { controller.abort(); resize?.disconnect(); cancelAnimationFrame(frame); });
  }

  let topButton;
  let scrollFrame = 0;
  function updateScroll() {
    scrollFrame = 0;
    const y = window.scrollY;
    document.documentElement.classList.toggle('aerfa-is-scrolled', y > 24);
    const progress = document.querySelector('.aerfa-scroll-progress');
    if (progress) progress.style.setProperty('--progress', String(Math.min(1, y / Math.max(1, document.documentElement.scrollHeight - innerHeight))));
    if (topButton) {
      const visible = y > 700;
      topButton.classList.toggle('is-visible', visible);
      topButton.tabIndex = visible ? 0 : -1;
      topButton.setAttribute('aria-hidden', String(!visible));
    }
    const hero = document.querySelector('[data-aerfa-hero]');
    if (hero) {
      const offset = !reduced.matches && fine.matches ? Math.min(36, Math.max(-12, -hero.getBoundingClientRect().top * .055)) : 0;
      hero.style.setProperty('--aerfa-parallax', String(offset));
    }
  }
  const scheduleScroll = () => { if (!scrollFrame) scrollFrame = requestAnimationFrame(updateScroll); };
  function init(root = document) {
    prepareReveals(root);
    all('[data-aerfa-motion-root]', root).forEach(host => {
      if (host.querySelector('.aerfa-scroll-progress')) return;
      const progress = document.createElement('div');
      progress.className = 'aerfa-scroll-progress';
      progress.setAttribute('aria-hidden', 'true');
      host.appendChild(progress);
    });
    all('[data-aerfa-rail-controls]', root).forEach(controls => {
      const rail = document.getElementById(controls.dataset.target);
      if (rail) prepareRail(rail, controls);
    });
    all('.product-content-section details', root).forEach(details => {
      if (seen.has(details)) return;
      seen.add(details);
      details.addEventListener('toggle', () => {
        if (details.open) Array.from(details.children).filter(child => child.tagName !== 'SUMMARY').forEach(child => animate(child));
      });
    });
    if (!topButton) {
      topButton = document.createElement('button');
      topButton.type = 'button';
      topButton.className = 'aerfa-back-top';
      topButton.setAttribute('aria-label', copy.top);
      topButton.title = copy.top;
      topButton.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M12 19V5m-6 6 6-6 6 6"/></svg>';
      topButton.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: reduced.matches ? 'instant' : 'smooth' });
        const destination = document.querySelector('header a[href]');
        destination?.focus({ preventScroll: true });
      });
      document.body.appendChild(topButton);
    }
    scheduleScroll();
  }
  window.addEventListener('scroll', scheduleScroll, { passive: true });
  window.addEventListener('resize', scheduleScroll, { passive: true });
  reduced.addEventListener('change', () => {
    if (reduced.matches) animations.forEach(animation => animation.cancel());
    scheduleScroll();
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    rails.forEach((cleanup, rail) => { if (event.target.contains(rail)) { cleanup(); rails.delete(rail); } });
    all('[data-aerfa-reveal], .aerfa-product-card', event.target).forEach(element => reveal?.unobserve(element));
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
